import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiRequest from "./AxiosConfiguration";

const initialState = {
  list: [],
  loading: false,
  error: null,
  selectedEmployee: null,
};

// Fetch all employees
export const fetchEmployees = createAsyncThunk(
  "employee/fetchEmployees",
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Employee/Employee_GetEmployeeList?CompanyId=${companyId}`,
      );
      if (res.success) {
        return res.data;
      } else {
        return rejectWithValue(res.error);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Fetch single employee by ID (for editing)
export const fetchEmployeeById = createAsyncThunk(
  "employee/fetchEmployeeById",
  async ({ empID, companyId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Employee/GetEmployee?empID=${empID}&CompanyId=${companyId}`,
      );
      if (res.success) {
        return res.data;
      } else {
        return rejectWithValue(res.error);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Create or Update employee
export const createUpdateEmployee = createAsyncThunk(
  "employee/createUpdateEmployee",
  async ({ employeeData, companyId }, { rejectWithValue }) => {
    try {
      const isEdit = employeeData.EmployeeID > 0;

      // Create FormData with correct property names
      const formData = new FormData();
      formData.append("EmpCode", employeeData.EmpCode || "");
      formData.append("EmployeeID", employeeData.EmployeeID);
      formData.append("EmployeeName", employeeData.EmployeeName);
      formData.append("ContactCode", employeeData.ContactCode || "");
      formData.append("ContactNumber", employeeData.ContactNumber || "");
      formData.append("EmailID", employeeData.EmailID);
      formData.append("UserName", employeeData.UserName);
      formData.append("Password", employeeData.Password);
      formData.append("RoleID", employeeData.RoleID);
      formData.append("IsActive", employeeData.IsActive ? 1 : 0);
      formData.append("SavedBy", employeeData.SavedBy);
      formData.append("SavedDate", employeeData.SavedDate);

      const res = await apiRequest(
        "POST",
        `Employee/Employee_CreateUpdateEmployee?CompanyId=${companyId}`,
        formData,
        true,
        false,
        false,
        false,
        isEdit,
      );

      if (res.success) {
        const message = res.data?.Message;

        if (message === "EMAIL_ALREADY_EXISTS") {
          return rejectWithValue({ message: "EMAIL_ALREADY_EXISTS" });
        }
        if (message === "PHONE_ALREADY_EXISTS") {
          return rejectWithValue({ message: "PHONE_ALREADY_EXISTS" });
        }
        if (message === "EMPLOYEE_MAPPED_WITH_SHIFT") {
          return rejectWithValue({ message: "EMPLOYEE_MAPPED_WITH_SHIFT" });
        }
        if (message === "ERROR") {
          return rejectWithValue({ message: "ERROR" });
        }

        return { data: res.data, isEdit };
      } else {
        return rejectWithValue(res.error);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Delete employee
export const deleteEmployee = createAsyncThunk(
  "employee/deleteEmployee",
  async ({ empId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Employee/DeleteEmployee?EmpId=${empId}`,
        null,
        false,
        false,
        true,
        false,
        false,
        {},
        false,
        false,
        false,
        false, // confirmation handled in UI
      );

      if (res.success) {
        if (Number(res.data) === -1) {
          return rejectWithValue({ mapped: true });
        }
        return { empId, message: res.data };
      } else {
        return rejectWithValue(res.error);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Check if username exists
export const checkUsernameExists = createAsyncThunk(
  "employee/checkUsernameExists",
  async ({ username }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Employee/ChkUserNameExist?rName=${username}`,
      );
      if (res.success) {
        const rawData = res.data;
        const exists =
          rawData === "1" ||
          rawData === 1 ||
          rawData === "true" ||
          rawData === true ||
          String(rawData).toLowerCase() === "true" ||
          String(rawData) === "1";

        return { exists, username };
      } else {
        return rejectWithValue(res.error);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Check if employee code exists
export const checkEmployeeCodeExists = createAsyncThunk(
  "employee/checkEmployeeCodeExists",
  async ({ code, companyId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Employee/ChkUserEmpCodeExist?Code=${code}&CompanyId=${companyId}`,
      );
      if (res.success) {
        // Normalize: handle "1", 1, "true", true
        const rawData = res.data;
        const exists =
          rawData === "1" ||
          rawData === 1 ||
          rawData === "true" ||
          rawData === true ||
          String(rawData).toLowerCase() === "true" ||
          String(rawData) === "1";

        return { exists, code };
      } else {
        return rejectWithValue(res.error);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Check if mobile number exists
export const checkMobileNumberExists = createAsyncThunk(
  "employee/checkMobileNumberExists",
  async ({ id, number, companyId }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Employee/ChkMobileNumberExists?ID=${id}&number=${number}&CompanyId=${companyId}`,
      );
      if (res.success) {
        const rawData = res.data;
        const exists =
          rawData === "1" ||
          rawData === 1 ||
          rawData === "true" ||
          rawData === true ||
          String(rawData).toLowerCase() === "true" ||
          String(rawData) === "1";

        return { exists, number };
      } else {
        return rejectWithValue(res.error);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const employeeSlice = createSlice({
  name: "employee",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedEmployee: (state) => {
      state.selectedEmployee = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load employees";
      })

      .addCase(fetchEmployeeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEmployee = action.payload;
      })
      .addCase(fetchEmployeeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load employee";
      })

      .addCase(createUpdateEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUpdateEmployee.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createUpdateEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to save employee";
      })

      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (emp) => emp.EmployeeID !== action.payload.empId,
        );
      });
  },
});

export const { clearError, clearSelectedEmployee } = employeeSlice.actions;
export default employeeSlice.reducer;
